<template>
  <button
    ref="item"
    type="button"
    class="bio-social-item"
    :class="{'bio-force-mobile': forceMobile}"
    :style="{color: descriptionColor, background: chipBackground}"
    :aria-label="handleDisplay ? `${link.title} ${handleDisplay}` : link.title"
    @click="$emit('open')"
    @mouseenter="onHoverEnter"
    @mouseleave="onHoverLeave"
    @mousedown="onPressStart"
    @mouseup="onPressEnd"
    @touchstart="onPressStart"
    @touchend="onPressEnd"
  >
    <v-icon :color="iconColor" size="20">{{ icon }}</v-icon>
    <span class="bio-social-item-text">
      <b class="bio-social-item-name" :style="{color: titleColor}">{{ link.title }}</b>
      <span v-if="handleDisplay" class="bio-social-item-handle">{{ handleDisplay }}</span>
    </span>
  </button>
</template>

<script>
import { gsap } from 'gsap'

export default {
  name: 'BioSocialLinkCard',
  props: {
    link: { type: Object, required: true },
    iconColor: { type: String, default: '#101827' },
    titleColor: { type: String, default: '#101827' },
    descriptionColor: { type: String, default: '#5B6472' },
    index: { type: Number, default: 0 },
    forceMobile: { type: Boolean, default: false },
  },
  computed: {
    // Soft tinted pill instead of the old bare-text-plus-divider row: each
    // item reads as its own chip (consistent with the overflow trigger in
    // BioSocialLinksGrid.vue, which uses the same "iconColor + alpha"
    // formula) rather than floating glyphs separated by a faint line.
    chipBackground: function() {
      return `${this.iconColor}14`
    },
    option: function() {
      return this.$t('socialOptions').filter(v => v.name == this.link.title)[0]
    },
    icon: function() {
      return this.option ? this.option.icon : 'mdi-link-variant'
    },
    // The handle isn't stored as its own field - it's whatever comes after
    // the network's base URL (e.g. "https://instagram.com/" + "someuser"),
    // so it's derived from the same onlineAddress.url the editor validates
    // against that base.
    handle: function() {
      const url = this.link.onlineAddress && this.link.onlineAddress.url
      if (!this.option || !url || !url.startsWith(this.option.url)) return null
      const rest = url.slice(this.option.url.length).split('?')[0].split('/')[0]
      return rest ? decodeURIComponent(rest) : null
    },
    handleDisplay: function() {
      if (!this.handle) return null
      return this.link.title === 'WhatsApp' ? this.handle : `@${this.handle}`
    },
  },
  data: function() {
    return { prefersReducedMotion: false }
  },
  mounted: function() {
    this.prefersReducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    const el = this.$refs.item
    if (this.prefersReducedMotion) {
      gsap.set(el, { opacity: 1, scale: 1 })
      return
    }
    // Same reveal-on-scroll approach as BioContentLinkRenderer.vue - see
    // that component for why IntersectionObserver over GSAP's
    // ScrollTrigger (this site has two different scroll containers
    // depending on breakpoint, and IntersectionObserver's root:null mode
    // handles that automatically).
    gsap.set(el, { opacity: 0, scale: 0.7 })
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return
      gsap.to(el, { opacity: 1, scale: 1, duration: 0.4, delay: Math.min(this.index, 8) * 0.05, ease: 'back.out(2)', clearProps: 'transform' })
      observer.disconnect()
    }, { threshold: 0.4 })
    observer.observe(el)
    this._revealObserver = observer
  },
  beforeDestroy: function() {
    if (this._revealObserver) this._revealObserver.disconnect()
  },
  methods: {
    onHoverEnter: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.item, { scale: 1.08, duration: 0.2, ease: 'power2.out' })
    },
    onHoverLeave: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.item, { scale: 1, duration: 0.2, ease: 'power2.out' })
    },
    onPressStart: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.item, { scale: 0.92, duration: 0.1 })
    },
    onPressEnd: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.item, { scale: 1, duration: 0.15 })
    },
  },
}
</script>

<style scoped>
.bio-social-item {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  height: 44px;
  max-width: 168px;
  padding: 0 0.9rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: filter 0.2s ease;
}
@media (hover: hover) {
  .bio-social-item:hover {
    filter: brightness(0.94);
  }
}
.bio-social-item:active {
  filter: brightness(0.88);
}
.bio-social-item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
  line-height: 1.15;
}
.bio-social-item-name {
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bio-social-item-handle {
  font-size: 0.7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Mobile/tablet (<1024px, same breakpoint the card as a whole switches
   on): icon only, name/handle text hidden - per feedback, the row of
   name+handle labels crowded the narrow width. Desktop keeps both. */
@media (max-width: 1023.98px) {
  .bio-social-item-text {
    display: none;
  }
  .bio-social-item {
    max-width: none;
    width: 44px;
    padding: 0;
    justify-content: center;
  }
}
/* The media query above only fires on a genuinely narrow real viewport -
   the editor's phone-mockup preview stays at the real (wide) editor
   viewport width and relies on forceMobile instead (see design.md's
   forceMobile note re: CSS media queries checking the real viewport, not
   an ancestor's rendered width). */
.bio-social-item.bio-force-mobile .bio-social-item-text {
  display: none;
}
.bio-social-item.bio-force-mobile {
  max-width: none;
  width: 44px;
  padding: 0;
  justify-content: center;
}
</style>
